import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  Activity,
  FileText,
  Package,
  Settings,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  key: string;
  icon: typeof Home;
  path: string;
  permission?: keyof typeof permissionMap;
}

const permissionMap = {
  view: 'view',
  edit: 'edit',
  schedule: 'schedule',
  billing: 'billing',
} as const;

const navItems: NavItem[] = [
  { key: 'dashboard', icon: Home, path: '/dashboard' },
  { key: 'patients', icon: Users, path: '/patients', permission: 'view' },
  { key: 'appointments', icon: Calendar, path: '/appointments', permission: 'schedule' },
  { key: 'treatments', icon: Activity, path: '/treatments', permission: 'view' },
  { key: 'invoices', icon: FileText, path: '/invoices', permission: 'billing' },
  { key: 'catalog', icon: Package, path: '/catalog', permission: 'edit' },
  { key: 'settings', icon: Settings, path: '/settings' },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { provider } = useAuth();

  const hasPermission = (permission?: keyof typeof permissionMap): boolean => {
    if (!permission || !provider) return true;
    return provider.permissions[permission] || false;
  };

  const visibleNavItems = navItems.filter((item) => hasPermission(item.permission));

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-gray-200
          transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Sidebar navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {t('app.name')}
                </h1>
                <p className="text-xs text-gray-500">{t('app.tagline')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={({ isActive }) =>
                    `
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-all duration-200 group
                    ${
                      isActive
                        ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }
                  `
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`h-5 w-5 ${
                          isActive ? 'text-white' : 'text-gray-500 group-hover:text-brand-primary'
                        }`}
                      />
                      <span className="font-medium">{t(`nav.${item.key}`)}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User info (if needed) */}
          {provider && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-brand-light rounded-full flex items-center justify-center">
                  <span className="text-brand-primary font-semibold text-sm">
                    {provider.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {provider.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{provider.role}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
